import React, { useCallback, useEffect, useState } from 'react';

import { TourProvider, useNavigate, useSelector, useTour } from 'third-party';

import {
  bankListUrl,
  memberListUrl,
  organizationListUrl,
  payeeListUrl,
  paymentListUrl,
} from 'constants/url';

import { useUser } from 'modules/user';

import { appSelectors, userSelectors } from 'selectors';

import {
  canStartTour,
  filterTourSteps,
  findTourOrganization,
} from 'utils/tourAccess';

import {
  APIUser,
  APIUserRight,
  APIUserSettingModel,
  APIUserSettingType,
} from 'types/api';

import { tourStyles } from 'styles/theme';

const tourOrganizationId = (user: APIUser) =>
  (findTourOrganization(user) ?? user.userOrganizations[0])?.organization.id;

type StepData = {
  selector: string;
  link?: (user: APIUser) => string;
  content: string;
  userRight?: APIUserRight;
};

export const allSteps: StepData[] = [
  {
    selector: '#menu-organization',
    link: () => organizationListUrl(),
    content:
      'Welcome to your organization. Here you can manage your organization settings and preferences.',
  },
  {
    selector: '#menu-organization-bank',
    userRight: APIUserRight.BankAccess,
    link: (user: APIUser) => bankListUrl(tourOrganizationId(user)),
    content:
      'This is where you create and manage banks. Personalize your check layouts - add logo, QR codes, signatures, and adjust formatting.',
  },
  {
    selector: '#menu-organization-payment',
    userRight: APIUserRight.PaymentProcessing,
    link: (user: APIUser) => paymentListUrl(tourOrganizationId(user)),
    content: 'This is where you create and track payments',
  },
  {
    selector: '#menu-organization-payee',
    userRight: APIUserRight.PayeeDirectory,
    link: (user: APIUser) => payeeListUrl(tourOrganizationId(user)),
    content:
      'Here you manage all payees in your organization. You can create new payees, edit or delete payees',
  },
  {
    selector: '#menu-organization-member',
    userRight: APIUserRight.MemberAdministration,
    link: (user: APIUser) => memberListUrl(tourOrganizationId(user)),
    content:
      'Here you manage all users in your organization. You can create new users, edit or delete users',
  },

  {
    selector: '#user-avatar',
    content:
      'This is your user avatar. Click here to access your account settings.',
  },
  {
    selector: '#info-links',
    content: 'Need more information? Check our help materials here.',
  },
];

export const useCheckTour = () => {
  const { isOpen, currentStep, steps, setIsOpen, setCurrentStep, setSteps } =
    useTour();
  const navigate = useNavigate();
  const user = useSelector(appSelectors.user);
  const userSettings = useSelector(userSelectors.userSettings);
  const userSettingsLoaded = useSelector(userSelectors.userSettingsLoaded);
  const { loadUserSettings, updateUserSettings, clearUserSettings } = useUser();

  const isTourCompleted = userSettings.some(
    (setting: APIUserSettingModel) =>
      setting.type === APIUserSettingType.UserTour &&
      setting.value === 'completed',
  );

  useEffect(() => {
    if (user) {
      loadUserSettings();
    } else {
      clearUserSettings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id ?? 'logged-out']);

  useEffect(() => {
    setSteps?.(filterTourSteps(allSteps, user));
  }, [user, setSteps]);

  useEffect(() => {
    if (user && userSettingsLoaded && !isTourCompleted && canStartTour(user)) {
      navigate(organizationListUrl());
      setIsOpen(true);
      completeTour();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, userSettingsLoaded, isTourCompleted, setIsOpen, navigate]);

  const completeTour = useCallback(() => {
    updateUserSettings({
      type: APIUserSettingType.UserTour,
      value: 'completed',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateUserSettings]);

  return {
    isOpen,
    currentStep,
    steps,
    setIsOpen,
    setCurrentStep,
    setSteps,
    completeTour,
    isTourCompleted,
  };
};

type Props = {
  children: React.ReactNode;
};

export const TourContainer: React.FC<Props> = ({ children }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const user = useSelector(appSelectors.user);
  const tourSteps = filterTourSteps(allSteps, user);

  const setCurrentStep = (step: number | ((prevStep: number) => number)) => {
    const stepNumber = typeof step === 'function' ? step(0) : step;
    const currentStep = tourSteps[stepNumber];
    if (currentStep && currentStep.link && user) {
      navigate(currentStep.link(user));
    }
    setTimeout(() => {
      setStep(typeof step === 'function' ? step(0) : step);
    }, 100);
  };

  return (
    <TourProvider
      steps={tourSteps}
      currentStep={step}
      setCurrentStep={setCurrentStep}
      styles={tourStyles}
    >
      {children}
    </TourProvider>
  );
};
